import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PAGINATION_COUNT_STEP_SIX } from "constant/index";
import {
	dowloadDocumentArchive,
	formDocumentPackage,
	getBatches,
	getBatchObjects,
	getData,
	getDocumentArchive,
	getSearchedBatchObjects,
	getSearchedData,
} from "./stepSix.thunks";
import { IArchiveData, IBatch, IData, IObject } from "./stepSix.types";

interface StepSixState {
	id: number | null;
	data: IData;
	searchValue: string;
	batchList: IBatch[];
	batchObjects: IData;
	currentBatch: IBatch | null;
	// selectionList: number[];
	excludedObjects: number[];
	attachmentList: number[];
	allChecked: boolean | null;
	allCheckedGlobal: boolean | null;
	formationMode: boolean;
	batchesMode: boolean;
	tableMode: boolean;
	archiveData: IArchiveData;
	newBatch: IBatch | null;
	searchedData: IData;
	searchMode: boolean;
	searchPending: boolean;
	lastPart: number | null;
	lastSearchedPart: number | null;
}

const initialState: StepSixState = {
	id: null,
	data: {
		categorized: [],
		uncategorized: [],
		repeatable: [],
	},
	batchList: [],
	batchObjects: {
		categorized: [],
		uncategorized: [],
		repeatable: [],
	},
	currentBatch: null,
	formationMode: false,
	excludedObjects: [],
	attachmentList: [],
	allChecked: null,
	allCheckedGlobal: null,
	batchesMode: false,
	tableMode: true,
	archiveData: {
		URL: "",
		formation: false,
	},
	newBatch: null,
	// search
	searchValue: "",
	searchedData: {
		categorized: [],
		uncategorized: [],
		repeatable: [],
	},
	searchMode: false,
	searchPending: false,
	// pagination
	lastPart: 0,
	lastSearchedPart: 0,
};

const stepSixSlice = createSlice({
	name: "stepSixSlice",
	initialState,
	reducers: {
		ChangeSearchValue(state, action: PayloadAction<string>) {
			state.searchValue = action.payload;
			state.lastSearchedPart = 0;
			state.searchedData = {
				categorized: [],
				uncategorized: [],
				repeatable: [],
			};

			if (!state.searchValue) {
				state.searchMode = false;
				state.searchPending = false;
				state.lastPart = 0;

				state.data = {
					categorized: [],
					uncategorized: [],
					repeatable: [],
				};
				state.batchObjects = {
					categorized: [],
					uncategorized: [],
					repeatable: [],
				};
			} else {
				state.searchMode = false;
				state.searchMode = true;
				state.searchPending = true;
			}
		},
		ChangeFormationMode(state, action: PayloadAction<boolean>) {
			state.formationMode = action.payload;
		},
		ChangeBatchesMode(state, action: PayloadAction<boolean>) {
			state.batchesMode = action.payload;
			if (!action.payload) {
				state.batchObjects = {
					categorized: [],
					uncategorized: [],
					repeatable: [],
				};
			}
		},
		ChangeTableMode(state, action: PayloadAction<boolean>) {
			state.tableMode = action.payload;
		},
		ChangeCurrentBatch(state, action: PayloadAction<IBatch>) {
			state.searchMode = false;
			state.searchPending = false;
			state.currentBatch = action.payload;
		},
		ClearCurrentBatch(state) {
			state.currentBatch = null;
		},
		// 1
		AddToAttachmentList(state, action: PayloadAction<number>) {
			if (!state.attachmentList.includes(action.payload)) {
				state.attachmentList.push(action.payload);
			}
		},
		// 2
		RemoveFromAttachmentList(state, action: PayloadAction<number>) {
			state.allChecked = null;
			state.attachmentList = state.attachmentList.filter((objId) => objId !== action.payload);
			if (state.allCheckedGlobal) {
				state.excludedObjects.push(action.payload);
			}
		},
		// 3
		ChangeChooseAll(state, action: PayloadAction<boolean | null>) {
			state.allChecked = action.payload;
			if (action.payload !== null && !action.payload) state.attachmentList = [];
			if (action.payload) {
				for (let i = 1; i <= 2; i++) {
					const localList =
						i === 1
							? (state.searchMode ? state.searchedData : state.data).categorized
							: (state.searchMode ? state.searchedData : state.data).repeatable;

					localList.forEach((object) => {
						if (!state.attachmentList.includes(object.id)) {
							state.attachmentList.push(object.id);
						}
					});
				}
			}
		},
		ChangeChooseAllGlobal(state, action: PayloadAction<boolean | null>) {
			if (state.searchMode) return;
			state.excludedObjects = [];
			state.allCheckedGlobal = action.payload;
		},
		// 4
		ClearAttachmentList(state) {
			state.allChecked = null;
			state.attachmentList = [];
		},
		ClearGlobalChecked(state) {
			state.excludedObjects = [];
			state.allCheckedGlobal = null;
		},
		ClearPaginationData(state, action: PayloadAction<{ clearBatches: boolean }>) {
			state.lastPart = 0;
			state.lastSearchedPart = 0;
			state.data = {
				categorized: [],
				uncategorized: [],
				repeatable: [],
			};
			state.searchedData = {
				categorized: [],
				uncategorized: [],
				repeatable: [],
			};
			if (action.payload.clearBatches) {
				state.batchObjects = {
					categorized: [],
					uncategorized: [],
					repeatable: [],
				};
			}
		},
		resetStep_6: (state) => ({ ...initialState, tableMode: state.tableMode }),
	},

	extraReducers: (builder) => {
		builder.addCase(getData.fulfilled, (state, action) => {
			state.data.categorized.push(...action.payload.categorized);
			state.data.repeatable.push(...action.payload.repeatable);
			state.data.uncategorized.push(...action.payload.uncategorized);

			let arrLength: IObject[] = [];
			arrLength = arrLength.concat(action.payload.categorized, action.payload.repeatable, action.payload.uncategorized);
			if (arrLength.length && state.lastPart !== null && arrLength.length === PAGINATION_COUNT_STEP_SIX)
				state.lastPart++;
			else {
				state.lastPart = null;
			}
		});
		builder.addCase(getSearchedData.fulfilled, (state, action: PayloadAction<IData>) => {
			state.searchedData.categorized.push(...action.payload.categorized);
			state.searchedData.repeatable.push(...action.payload.repeatable);
			state.searchedData.uncategorized.push(...action.payload.uncategorized);
			state.searchPending = false;

			let arrLength: IObject[] = [];
			arrLength = arrLength.concat(action.payload.categorized, action.payload.repeatable, action.payload.uncategorized);
			if (arrLength.length && state.lastSearchedPart !== null && arrLength.length === PAGINATION_COUNT_STEP_SIX)
				state.lastSearchedPart++;
			else {
				state.lastSearchedPart = null;
			}
		});
		builder.addCase(getBatchObjects.fulfilled, (state, action: PayloadAction<IData>) => {
			state.batchObjects.categorized.push(...action.payload.categorized);
			state.batchObjects.repeatable.push(...action.payload.repeatable);
			state.batchObjects.uncategorized.push(...action.payload.uncategorized);

			let arrLength: IObject[] = [];
			arrLength = arrLength.concat(action.payload.categorized, action.payload.repeatable, action.payload.uncategorized);
			if (arrLength.length && state.lastPart !== null && arrLength.length === PAGINATION_COUNT_STEP_SIX)
				state.lastPart++;
			else {
				state.lastPart = null;
			}
		});
		builder.addCase(getSearchedBatchObjects.fulfilled, (state, action: PayloadAction<IData>) => {
			state.searchedData.categorized.push(...action.payload.categorized);
			state.searchedData.repeatable.push(...action.payload.repeatable);
			state.searchedData.uncategorized.push(...action.payload.uncategorized);
			state.searchPending = false;

			let arrLength: IObject[] = [];
			arrLength = arrLength.concat(action.payload.categorized, action.payload.repeatable, action.payload.uncategorized);
			if (arrLength.length && state.lastSearchedPart !== null && arrLength.length === PAGINATION_COUNT_STEP_SIX)
				state.lastSearchedPart++;
			else {
				state.lastSearchedPart = null;
			}
		});
		builder.addCase(getBatches.fulfilled, (state, action: PayloadAction<IBatch[]>) => {
			state.batchList = action.payload;
		});
		builder.addCase(formDocumentPackage.fulfilled, (state, action: PayloadAction<any>) => {
			state.newBatch = action.payload;
			state.archiveData.URL = "";
			state.archiveData.formation = true;
			state.allChecked = null;
			state.allCheckedGlobal = null;
			state.attachmentList = [];
			state.excludedObjects = [];
		});
		builder.addCase(getDocumentArchive.fulfilled, (state, action: PayloadAction<any>) => {
			if (action.payload) {
				state.archiveData.URL = action.payload;
				state.archiveData.formation = false;
			} else {
				state.archiveData.URL = "";
				state.archiveData.formation = true;
			}
		});
		builder.addCase(dowloadDocumentArchive.fulfilled, (state, action: PayloadAction<any>) => {
			state.archiveData.URL = "";
			state.archiveData.formation = false;
			state.newBatch = null;
		});
	},
});

export const {
	ChangeSearchValue,
	ChangeCurrentBatch,
	ClearCurrentBatch,
	ChangeFormationMode,
	ChangeBatchesMode,
	ChangeTableMode,
	AddToAttachmentList,
	RemoveFromAttachmentList,
	ChangeChooseAll,
	ChangeChooseAllGlobal,
	ClearAttachmentList,
	ClearGlobalChecked,
	ClearPaginationData,
	resetStep_6,
} = stepSixSlice.actions;
export default stepSixSlice.reducer;
